import styled from "@emotion/styled";
import React, { ReactNode, useState } from "react";
import {
  Box,
  Collapse,
  TableCell,
  TableRow as MuiTableRow,
  TableRowProps,
  Theme,
} from "@mui/material";
import ArrowDown from "@icons/myicons/arrow-down.svg?react";
import { TableBodyCell } from "@components/Table/TableBodyCell";

const getBorderBottomStyle = ({
  theme,
  isExpandable,
  isExpanded,
  isSelected,
}: {
  theme: Theme;
  isExpandable?: boolean;
  isExpanded?: boolean;
  isSelected?: boolean;
}) => {
  if (isExpandable && isExpanded) {
    return `1px solid transparent`;
  }
  if (isSelected) return `1px solid ${theme.palette.border.secondary}`;
  return `1px solid ${theme.palette.border.secondary}`;
};

const Container = styled(MuiTableRow, {
  shouldForwardProp: (prop) =>
    prop !== "isExpanded" &&
    prop !== "isExpandable" &&
    prop !== "isSelected" &&
    prop !== "isClickable" &&
    prop !== "backgroundColor",
})<{
  isExpandable?: boolean;
  isExpanded?: boolean;
  isSelected?: boolean;
  isClickable?: boolean;
}>`
  & > td {
    border-bottom: ${({ theme, isExpandable, isExpanded, isSelected }) =>
      getBorderBottomStyle({ theme, isExpandable, isExpanded, isSelected })};
  }
  & > td.expand {
    padding: ${({ theme }) => theme.spacing(2, 0, 2, 0)};
    width: 32px;
  }
  &:has(> td.expanded-row) {
    & > td {
      border-bottom-color: transparent !important;
    }
  }
  & > td.expanded-row {
    padding: 0;
  }
  cursor: ${({ isExpandable = false, isClickable = false }) =>
    isExpandable || isClickable ? "pointer" : "inherit"};
  background: ${({ theme, isSelected }) =>
    isSelected ? `${theme.palette.background.neutral}` : "unset"};
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease;
  & > td {
    transition: border-color 0.2s ease;
  }
  ${({ isExpandable, isClickable, theme }) =>
    (isExpandable || isClickable) &&
    `
    ${
      !isExpandable
        ? `
    &:hover {
      background: ${theme.palette.background.subtle};
      border-radius: ${theme.shape.borderRadius}px;
      box-shadow: ${theme.shadows[1]};
      & > td {
        border-bottom-color: transparent;
      }
    }
    &:hover > td:first-of-type {
      border-radius: ${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px;
    }
    &:hover > td:last-of-type {
      border-radius: 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0;
    }
    `
        : ""
    }
    ${
      !isExpandable
        ? `
    &:has(+ tr[data-hoverable]:hover) > td {
      border-bottom-color: transparent;
    }
    `
        : ""
    }
  `}
`;

interface Props extends TableRowProps {
  className?: string;
  children: ReactNode;
  expandableElement?: ReactNode;
  isSelected?: boolean;
  isClickable?: boolean;
  verticalAlign?: "top" | "middle" | "bottom";
  isExpanded?: boolean;
  arrowPosition?: "first" | "last" | "none";
  isExpandedInTableCell?: boolean;
  reserveArrowSlot?: boolean;
}

export const TableRow = ({
  className,
  children,
  expandableElement,
  isSelected,
  isClickable,
  verticalAlign = "middle",
  onClick,
  isExpanded: isOutsideExpanded,
  arrowPosition = "first",
  isExpandedInTableCell = true,
  reserveArrowSlot = false,
  ...props
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(isOutsideExpanded ?? false);
  const [prevOutsideExpanded, setPrevOutsideExpanded] =
    useState(isOutsideExpanded);
  if (prevOutsideExpanded !== isOutsideExpanded) {
    setPrevOutsideExpanded(isOutsideExpanded);
    if (isOutsideExpanded !== undefined) setIsExpanded(isOutsideExpanded);
  }

  const handleClick: TableRowProps["onClick"] = (...args) => {
    onClick?.(...args);
    if (!expandableElement) return;
    setIsExpanded((current) => !current);
  };

  const renderArrow = () => (
    <TableBodyCell className="expand" width={20}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        color="icon.secondary"
        sx={{
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          transformOrigin: "center center",
          transition: "transform 0.3s",
        }}
      >
        <ArrowDown />
      </Box>
    </TableBodyCell>
  );

  const renderArrowPlaceholder = () => (
    <TableBodyCell className="expand" width={20}>
      {null}
    </TableBodyCell>
  );

  const showArrow = Boolean(expandableElement);
  const showArrowSlot = showArrow || reserveArrowSlot;

  return (
    <>
      <Container
        isExpanded={isExpanded}
        className={isSelected ? `calendar-table-selected-row` : className}
        isExpandable={!!expandableElement}
        isClickable={isClickable}
        isSelected={isSelected}
        data-hoverable={isClickable && !expandableElement ? "true" : undefined}
        {...props}
        onClick={handleClick}
        sx={[
          { "&>td": { verticalAlign: `${verticalAlign} !important` } },
          ...(Array.isArray(props.sx) ? props.sx : props.sx ? [props.sx] : []),
        ]}
      >
        {showArrowSlot &&
          arrowPosition === "first" &&
          (showArrow ? renderArrow() : renderArrowPlaceholder())}
        {children}
        {showArrowSlot &&
          arrowPosition === "last" &&
          (showArrow ? renderArrow() : renderArrowPlaceholder())}
      </Container>
      {expandableElement && (
        <>
          {isExpandedInTableCell ? (
            <Container isExpanded={isExpanded} isExpandable>
              <TableCell colSpan={42} className="expanded-row">
                <Collapse in={isExpanded}>{expandableElement}</Collapse>
              </TableCell>
            </Container>
          ) : (
            isExpanded && expandableElement
          )}
        </>
      )}
    </>
  );
};
